import type { Skapi } from "skapi-js";

export type SkapiEndpoints = {
    admin_private_endpoint: string;
    admin_public_endpoint: string;
    record_private_endpoint: string;
    record_public_endpoint: string;
};

let resolveEndpoints: (value: SkapiEndpoints) => void;
let rejectEndpoints: (reason: unknown) => void;

/**
 * The SDK's endpoint urls, as a promise that exists from the moment this module
 * loads. It used to be undefined until initializeEndpoints() ran from main.ts,
 * which is later than the first awaiter: service-spec.ts fetches GET /plan while
 * it is being evaluated, read `undefined`, and gave up with "no admin endpoint".
 * A page opened straight on a project (a payment provider's return url, a
 * bookmark) has no plan cards to retry it, so Plan & Usage showed blank limits.
 * Now an early awaiter simply waits for main.ts to build the SDK.
 */
export let ENDPOINTS: Promise<SkapiEndpoints> = new Promise((resolve, reject) => {
    resolveEndpoints = resolve;
    rejectEndpoints = reject;
});

export function initializeEndpoints(skapi: Skapi): void {
    const internals = skapi as unknown as {
        admin_endpoint: Promise<any>;
        record_endpoint: Promise<any>;
    };

    const next = Promise.all([
        internals.admin_endpoint,
        internals.record_endpoint,
    ]).then(([admin, record]) => ({
        admin_private_endpoint: admin.admin_private,
        admin_public_endpoint: admin.admin_public,
        record_private_endpoint: record.record_private,
        record_public_endpoint: record.record_public,
    }));

    // The first call settles the promise the early awaiters hold; a later call
    // (main.ts rebuilding the SDK over a still-mounted app) hands every new
    // awaiter the new instance's urls through the live binding.
    next.then(resolveEndpoints, rejectEndpoints);
    ENDPOINTS = next;
}
